import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Mail, Phone } from "lucide-react";
import { EASE } from "./Reveal";
import { CONTACT } from "./content";
import { scrollToId } from "../../lib/lenis";

const LINES = [
  { t: "Keys in hand.", c: "text-bone" },
  { t: "Costs in", c: "text-bone" },
  { t: "check.", c: "text-lane" },
];

const MaskLine = ({ text, className, i }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "105%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay: 0.25 + i * 0.14 }}
    >
      {text}
    </motion.span>
  </span>
);

const TiltFrame = ({ scrollY }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <motion.div
      className="relative [perspective:1200px]"
      onMouseMove={onMove}
      onMouseLeave={reset}
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="clip-shield relative aspect-[4/5] overflow-hidden bg-graphite" data-testid="hero-image-frame">
          <motion.img src="/img/hero.jpg" alt="Teen driver behind the wheel" style={{ y: scrollY }} className="absolute inset-0 h-[118%] w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/10 to-transparent" />
        </div>
        <div style={{ transform: "translateZ(60px)" }} className="absolute -left-4 bottom-10 rounded-2xl border border-white/15 bg-asphalt/80 p-4 backdrop-blur-xl sm:-left-10" data-testid="hero-floating-card">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-lane">4 discounts to ask about</p>
          <p className="mt-1 font-display text-3xl font-extrabold uppercase text-bone">Stack them.</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "6%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <section id="top" ref={ref} onMouseMove={onMove} className="spotlight relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="road-lines pointer-events-none absolute inset-0 opacity-60" />
      <div className="relative mx-auto grid max-w-[1400px] gap-14 px-5 pb-20 pt-12 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-20">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.button
            onClick={() => scrollToId("program")}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="group inline-flex items-center gap-3 rounded-full border border-lane/40 bg-lane/10 py-1.5 pl-1.5 pr-4 text-left"
            data-testid="hero-announcement-pill"
          >
            <span className="rounded-full bg-lane px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-asphalt">New</span>
            <span className="font-body text-xs text-bone sm:text-sm">Announcing the ShieldTeen Safe Driver Program</span>
            <ArrowDownRight className="h-4 w-4 text-lane transition-transform duration-300 group-hover:rotate-[-45deg]" />
          </motion.button>
          <h1 className="mt-8 font-display text-[19vw] font-black uppercase leading-[0.86] tracking-tight sm:text-8xl lg:text-[8.6rem]" data-testid="hero-title">
            {LINES.map((l, i) => (
              <MaskLine key={l.t} text={l.t} className={l.c} i={i} />
            ))}
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.9 }} className="mt-8 max-w-xl font-body text-base leading-relaxed text-mist sm:text-lg">
            Your teen just earned their license. You just saw the insurance quote. Breathe. Good grades, driver ed, college moves and safe-driving apps can all bring that number down, and we'll explain every one.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 1.05 }} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <a href={CONTACT.tel} data-testid="hero-call-btn" className="group inline-flex items-center justify-center gap-3 rounded-full bg-lane px-7 py-4 font-body font-bold text-asphalt transition-transform duration-300 hover:-translate-y-1">
              <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" /> Call {CONTACT.phone}
            </a>
            <button onClick={() => scrollToId("stacker")} data-testid="hero-email-btn" className="inline-flex items-center justify-center gap-3 rounded-full border border-white/20 px-7 py-4 font-body font-semibold text-bone transition-colors duration-300 hover:border-lane hover:text-lane">
              <Mail className="h-5 w-5" /> Email for a teen quote
            </button>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 lg:pt-6">
          <TiltFrame scrollY={imgY} />
        </div>
      </div>
    </section>
  );
};
